import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { tokens } from "../../../../theme";
import CustomTextField from "../../../../components/ui/Textfield";
import apiService from "../../../../service/Api";
import { IContactFormData } from "../../../../types/contact";

interface AddCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (company: IContactFormData) => void;
}

const EMPTY = { name: "", email: "", phone: "" };

const AddCompanyDialog: React.FC<AddCompanyDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const isDarkMode = theme.palette.mode === "dark";
  const color400 = colors.primary[400] || "#724E41";
  const color10 = colors.primary[10] || "#C29363";

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const set = (field: keyof typeof EMPTY, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setForm(EMPTY);
    onClose();
  };

  const isValid = !!form.name.trim();

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const res = await apiService.post<IContactFormData>(
        "/api/contacts/contact/",
        {
          ...form,
          contact_type: "company",
        },
      );
      toast.success("Company added successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      onSaved(res.data);
      handleClose();
    } catch (error: unknown) {
      const data = (error as { response?: { data?: unknown } })?.response?.data;
      let message = "Failed to add company.";
      if (data && typeof data === "object") {
        message = Object.values(data).flat().join(" ") || message;
      }
      toast.error(message, { position: "top-right", autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>Add New Company</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <CustomTextField
            label="Company Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            required
            autoFocus
          />
          <CustomTextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
          <MuiTelInput
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={form.phone}
            defaultCountry="ZW"
            onChange={(value) => set("phone", value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{ color: isDarkMode ? colors.primary[200] : "#4A5568" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !isValid}
          sx={{
            background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            boxShadow: "none",
            "&:hover": { opacity: 0.9, boxShadow: "none" },
          }}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Save Company"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCompanyDialog;
